import { Injectable } from '@angular/core';
import { Platform } from '@ionic/angular';
import { Storage } from '@ionic/storage';

type LogGroup = 'system' | 'scanner';
type LogStore = Record<LogGroup, any[]>;

const LOG_KEY = 'app-logs-v1';
const LIMITS: Record<LogGroup, number> = { system: 1000, scanner: 500 };

let activeLogger: AppLogService | undefined;

@Injectable({ providedIn: 'root' })
export class AppLogService {
  private logs: LogStore = { system: [], scanner: [] };
  private writeChain = Promise.resolve();

  constructor(private storage: Storage, private platform: Platform) {
    activeLogger = this;
    void this.initialize();
  }

  read(group: LogGroup = 'system'): any[] {
    return [...this.logs[group]];
  }

  append(entry: any, group: LogGroup = 'system'): void {
    this.logs[group].push(entry);
    this.trim(group);
    this.persist();
  }

  replace(entries: any[], group: LogGroup = 'system'): void {
    this.logs[group] = Array.isArray(entries) ? [...entries] : [];
    this.trim(group);
    this.persist();
  }

  clear(group: LogGroup): void {
    this.logs[group] = [];
    this.persist();
  }

  private async initialize(): Promise<void> {
    try {
      await this.platform.ready();
      const stored = await this.storage.get(LOG_KEY);
      const saved: LogStore = this.isStore(stored) ? stored : this.readLegacy();
      this.logs = {
        system: [...(saved.system ?? []), ...this.logs.system],
        scanner: [...(saved.scanner ?? []), ...this.logs.scanner],
      };
      this.trim('system');
      this.trim('scanner');
      await this.storage.set(LOG_KEY, this.logs);
      localStorage.removeItem('log');
      localStorage.removeItem('logScanner');
    } catch (error) {
      console.error('Application log storage initialization failed.', error);
    }
  }

  private persist(): void {
    this.writeChain = this.writeChain
      .then(async () => {
        await this.platform.ready();
        return this.storage.set(LOG_KEY, this.logs);
      })
      .catch(error => console.error('Application log storage write failed.', error));
  }

  private trim(group: LogGroup): void {
    if (this.logs[group].length > LIMITS[group]) {
      this.logs[group] = this.logs[group].slice(-LIMITS[group]);
    }
  }

  private readLegacy(): LogStore {
    return {
      system: this.parseLegacy('log'),
      scanner: this.parseLegacy('logScanner'),
    };
  }

  private parseLegacy(key: string): any[] {
    try {
      const value = localStorage.getItem(key);
      const parsed = value ? JSON.parse(value) : [];
      return Array.isArray(parsed) ? parsed : [];
    } catch (_) {
      return [];
    }
  }

  private isStore(value: any): value is LogStore {
    return !!value && Array.isArray(value.system) && Array.isArray(value.scanner);
  }
}

export const appendAppLog = (entry: any, group: LogGroup = 'system') => activeLogger?.append(entry, group);
export const readAppLogs = (group: LogGroup = 'system') => activeLogger?.read(group) ?? [];
export const replaceAppLogs = (entries: any[], group: LogGroup = 'system') => activeLogger?.replace(entries, group);
export const clearAppLogs = (group: LogGroup) => activeLogger?.clear(group);
